//! nordy - 업스트림 위젯 구조 기반 + 노르디 업로드/인증/에셋 선택 로직
import { useNodeImage, useNodeVideo } from '@/composables/node/useNodeImage'
import { useNodeDragAndDrop } from '@/composables/node/useNodeDragAndDrop'
import { useNodeFileInput } from '@/composables/node/useNodeFileInput'
import { useNodePaste } from '@/composables/node/useNodePaste'
import type { LGraphNode } from '@/lib/litegraph/src/litegraph'
import type { IComboWidget } from '@/lib/litegraph/src/types/widgets'
import SelectInputAssetModal from '@/nordy/components/SelectInputAssetModal.vue'
import { AssetKind } from '@/nordy/interfaces'
import { useNordyModalStore } from '@/nordy/store/nordyModalStore'
import { useNordyUserStore } from '@/nordy/store/nordyUserStore'
import { fileUrlByAsset } from '@/nordy/utils/fileBucketUrl'
import { nordyApi } from '@/nordy/api'
import { useToastStore } from '@/platform/updates/common/toastStore'
import type { InputSpec } from '@/schemas/nodeDefSchema'
import type { ComfyWidgetConstructor } from '@/scripts/widgets'
import { useDialogStore } from '@/stores/dialogStore'
import { useNodeOutputStore } from '@/stores/nodeOutputStore'
import { isImageUploadInput } from '@/types/nodeDefAugmentation'
import { addToComboValues } from '@/utils/litegraphUtil'
import type { AxiosError } from 'axios'

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp'
const ACCEPTED_VIDEO_TYPES = 'video/webm,video/mp4'

//! nordy - 파일 크기 제한
const MAX_FILE_SIZE_MB = 20
const MAX_VIDEO_SIZE_MB = 40
const MAX_FILE_SIZE_BYTES: Record<AssetKind.Image | AssetKind.Video, number> = {
  [AssetKind.Image]: MAX_FILE_SIZE_MB * 1024 * 1024,
  [AssetKind.Video]: MAX_VIDEO_SIZE_MB * 1024 * 1024
}

const isImageFile = (file: File) => file.type.startsWith('image/')
const isVideoFile = (file: File) => file.type.startsWith('video/')

const findFileComboWidget = (
  node: LGraphNode,
  inputName: string
): IComboWidget | undefined =>
  node.widgets?.find((w): w is IComboWidget => w.name === inputName)

//! nordy - 로그인 확인
function requireLogin(): boolean {
  if (!useNordyUserStore().nordyUser) {
    useNordyModalStore().openLoginModal()
    return false
  }
  return true
}

//! nordy - 에셋 종류 헬퍼
function getAssetKind(isVideo: boolean): AssetKind.Image | AssetKind.Video {
  return isVideo ? AssetKind.Video : AssetKind.Image
}

function getMaxSizeMB(assetKind: AssetKind.Image | AssetKind.Video): number {
  return assetKind === AssetKind.Video ? MAX_VIDEO_SIZE_MB : MAX_FILE_SIZE_MB
}

export const useImageUploadWidget = () => {
  const widgetConstructor: ComfyWidgetConstructor = (
    node: LGraphNode,
    inputName: string,
    inputData: InputSpec
  ) => {
    if (!isImageUploadInput(inputData)) {
      throw new Error(
        'Image upload widget requires imageInputName augmentation'
      )
    }

    const inputOptions = inputData[1]
    const { imageInputName, allow_batch } = inputOptions
    const nodeOutputStore = useNodeOutputStore() //! nordy - upstream 스토어 사용

    const isAnimated = !!inputOptions.animated_image_upload
    const isVideo = !!inputOptions.video_upload
    const accept = isVideo ? ACCEPTED_VIDEO_TYPES : ACCEPTED_IMAGE_TYPES
    const { showPreview } = isVideo ? useNodeVideo(node) : useNodeImage(node)

    const fileFilter = isVideo ? isVideoFile : isImageFile
    const maybeWidget = findFileComboWidget(node, imageInputName)
    if (!maybeWidget) {
      throw new Error(`Widget "${imageInputName}" not found on node`)
    }
    const fileComboWidget: IComboWidget = maybeWidget

    //! nordy - 콤보 위젯 옵션 초기화 (노르디 전용 버튼과 함께 표시)
    if (!fileComboWidget.options) fileComboWidget.options = { values: [] }

    //! nordy - 에셋 종류
    const assetKind = getAssetKind(isVideo)

    //! nordy - 파일 크기 검증
    async function validateFileSize(file: File): Promise<boolean> {
      const maxBytes = MAX_FILE_SIZE_BYTES[assetKind]
      if (file.size <= maxBytes) return true

      useToastStore().add({
        severity: 'error',
        summary: 'File size exceeds the limit',
        detail: `File size must not exceed ${getMaxSizeMB(assetKind)}MB. Please select a smaller file.`,
        life: 10000
      })

      return false
    }

    //! nordy - 노르디 API 파일 업로드 (단일 파일)
    //! nordy - 전역 풀스크린 스피너를 쓰지 않는다. 업로드 진행 표시는
    //   handleUploadBatch의 노드 단위 상태(node.isUploading)가 담당한다.
    async function uploadFile(file: File): Promise<string | undefined> {
      if (!(await validateFileSize(file))) return

      try {
        const asset = await nordyApi.createInputAsset({ file, assetKind })
        const assetUrl = fileUrlByAsset({ asset })
        if (!assetUrl) return

        addToComboValues(fileComboWidget, assetUrl)

        useToastStore().add({
          severity: 'success',
          summary: 'Successfully uploaded file',
          life: 2000
        })

        return assetUrl
      } catch (error) {
        const status = (error as AxiosError)?.response?.status
        if (status === 413) {
          useToastStore().add({
            severity: 'error',
            summary: 'File size exceeds the limit',
            detail: `File size must not exceed ${getMaxSizeMB(assetKind)}MB. Please select a smaller file.`,
            life: 10000
          })
        } else {
          useToastStore().addAlert(String(error))
        }
      }
    }

    //! nordy - 업로드 배치 핸들러 (upstream handleUploadBatch 패턴)
    //! nordy - 전역 풀스크린 스피너(App.vue BlockUI) 대신 노드 단위 업로드 상태를 쓴다.
    //   전역 스피너는 업로드가 진행되는 동안 캔버스 전체를 막아, 느리거나 멈춘 업로드 시
    //   화면이 멈춘 것처럼 보였다. 업스트림처럼 node.isUploading만 토글한다.
    async function handleUploadBatch(files: File[]): Promise<string[]> {
      if (!requireLogin()) return []

      //! nordy - 같은 노드에서 업로드가 진행 중이면 중복 실행을 막는다.
      if (node.isUploading) {
        useToastStore().add({
          severity: 'warn',
          summary: 'Upload already in progress',
          life: 3000
        })
        return []
      }

      node.isUploading = true
      node.graph?.setDirtyCanvas(true)
      try {
        const uploaded: string[] = []
        for (const file of files) {
          const url = await uploadFile(file)
          if (url) uploaded.push(url)
          if (!allow_batch) break
        }

        if (uploaded.length > 0) {
          const newValue = allow_batch ? uploaded : uploaded[0]
          // @ts-expect-error litegraph combo value type does not support arrays yet
          fileComboWidget.value = newValue
          fileComboWidget.callback?.(newValue)
        }

        return uploaded
      } finally {
        node.isUploading = false
        node.graph?.setDirtyCanvas(true)
      }
    }

    //! nordy - 에셋 선택 모달 열기 함수
    function openAssetSelectModal() {
      if (!requireLogin()) return

      useDialogStore().showDialog({
        key: 'nordy-select-image',
        component: SelectInputAssetModal,
        props: {
          assetKind,
          onAssetSelected: (inputUrl: string) => {
            addToComboValues(fileComboWidget, inputUrl)
            fileComboWidget.value = inputUrl
            fileComboWidget.callback?.(inputUrl)
          }
        },
        dialogComponentProps: {
          closeOnEscape: true,
          modal: true
        }
      })
    }

    //! nordy - 콤보 위젯 클릭 시 에셋 선택 모달 열기
    fileComboWidget.options.onSelectClick = openAssetSelectModal

    //! nordy - 에셋 선택 모달 버튼
    const nordySelectWidget = node.addWidget(
      'button',
      inputName,
      'image',
      openAssetSelectModal,
      { serialize: false }
    )
    nordySelectWidget.name = 'choose file'

    //! nordy - 업스트림 composable로 드래그앤드롭/붙여넣기/파일입력 통합
    useNodeDragAndDrop(node, {
      fileFilter,
      onDrop: handleUploadBatch
    })

    useNodePaste(node, {
      fileFilter,
      allow_batch,
      onPaste: handleUploadBatch
    })

    const { openFileSelection } = useNodeFileInput(node, {
      fileFilter,
      allow_batch,
      accept,
      onSelect: handleUploadBatch
    })

    //! nordy - 콤보 위젯 업로드 버튼 클릭 시 파일 선택
    fileComboWidget.options.onUploadClick = () => {
      if (!requireLogin()) return
      openFileSelection()
    }

    //! nordy - 로컬 파일 직접 업로드 버튼
    const uploadWidget = node.addWidget(
      'button',
      inputName,
      'image',
      () => {
        if (!requireLogin()) return
        openFileSelection()
      },
      { serialize: false }
    )
    uploadWidget.label = 'File Direct Upload'

    //! nordy - 콤보 위젯 값 변경 시 프리뷰 렌더링 (upstream 패턴)
    fileComboWidget.callback = function () {
      node.imgs = undefined
      const selectedValue = String(fileComboWidget.value ?? '')
      //! nordy - S3 URL만 프리뷰 지원 (로컬 /api/view 미지원)
      if (selectedValue && selectedValue.startsWith('http')) {
        nodeOutputStore.setNodeOutputs(node, selectedValue, {
          isAnimated
        })
        //! nordy - 비디오 선택 시 프리뷰를 다시 로드하여 비율에 맞게 렌더링
        showPreview()
      }
      node.graph?.setDirtyCanvas(true)
    }

    //! nordy - 초기 로드 시 프리뷰 표시 (upstream 패턴)
    //! nordy - 값이 없거나 S3 URL이 아니면 프리뷰 스킵 (로컬 /api/view 미지원)
    requestAnimationFrame(() => {
      const currentValue = String(fileComboWidget.value ?? '')
      if (!currentValue || !currentValue.startsWith('http')) return

      nodeOutputStore.setNodeOutputs(node, currentValue, {
        isAnimated
      })
      showPreview({ block: false })
    })

    return { widget: uploadWidget }
  }

  return widgetConstructor
}
